import { publicAssetUrl } from "../config";
import { DEFAULT_BRAND, applyCountryLangBrandDefaults } from "../constants/brandDefaults";
import { LAST_BRAND_CLUB_ID_KEY } from "./localStorageBrand";
import { corsSafeLogoUrl, resolveClubImageUrl, sanitizeBrandLogoUrl } from "./resolveClubImageUrl";
import { clubLanguageFromStoredUser } from "./clubCountryLanguage";
import { applyBrandWebAppManifest } from "../pwa/applyBrandManifest";

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return "8, 119, 188";
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function headIconHref(url) {
  if (!url) return url;
  const value = String(url).trim();
  if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) return value;
  if (
    value === "/appicon.png" ||
    value === "appicon.png" ||
    value === "/favicon.ico" ||
    value === "favicon.ico"
  ) {
    return publicAssetUrl("appicon.png");
  }
  return value;
}

/** Inject all club CSS variables used across the app shell. */
export function injectBrandCssVariables(brand) {
  const root = document.documentElement;
  const b = brand || DEFAULT_BRAND;
  root.style.setProperty("--brand-primary", b.color_primary);
  root.style.setProperty("--brand-primary-dark", b.color_primary_dark);
  root.style.setProperty("--brand-primary-light", b.color_primary_light);
  root.style.setProperty("--brand-secondary", b.color_secondary);
  root.style.setProperty("--brand-accent", b.color_accent);
  root.style.setProperty("--brand-bg", b.color_background);
  root.style.setProperty("--brand-surface", b.color_surface);
  root.style.setProperty("--brand-text", b.color_text_primary);
  root.style.setProperty("--brand-text-secondary", b.color_text_secondary);
  root.style.setProperty("--brand-font", b.font_family || "Poppins");
  if (b.color_primary) {
    root.style.setProperty("--brand-primary-rgb", hexToRgb(b.color_primary));
  }
  root.style.setProperty("--Primary-Color", b.color_primary);
  if (b.color_background) {
    root.style.setProperty("--bg-color", b.color_background);
  }
  if (b.color_surface) {
    root.style.setProperty("--surface-color", b.color_surface);
  } else if (b.color_background) {
    root.style.setProperty("--surface-color", b.color_background);
  }
  if (b.color_text_primary) {
    root.style.setProperty("--text-color", b.color_text_primary);
  }
  if (b.color_text_secondary) {
    root.style.setProperty("--text-secondary", b.color_text_secondary);
  }
  if (b.font_family) {
    document.body.style.fontFamily = b.font_family;
  }
}

/** Update browser tab title, favicon, theme-color, description, and PWA install branding. */
export function updateBrandDocumentHead(brand) {
  const b = brand || DEFAULT_BRAND;
  document.title = b.brand_name || "Smart TT";

  const iconHref = headIconHref(
    corsSafeLogoUrl(
      resolveClubImageUrl(b.logo_url) ||
        resolveClubImageUrl(b.logo_light_url) ||
        resolveClubImageUrl(b.favicon_url) ||
        b.favicon_url ||
        b.logo_url
    )
  );
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon && iconHref) favicon.href = iconHref;

  const shortcut = document.querySelector('link[rel="shortcut icon"]');
  if (shortcut && iconHref) shortcut.href = iconHref;

  const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleTouchIcon && iconHref) appleTouchIcon.href = iconHref;

  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc && b.meta_description) metaDesc.content = b.meta_description;

  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor && b.color_primary) metaThemeColor.content = b.color_primary;

  // Home-screen name + icon come from the web app manifest / apple meta tags.
  try {
    const applied = applyBrandWebAppManifest(b);
    if (applied && typeof applied.then === "function") {
      applied.catch(() => undefined);
    }
  } catch {
    /* ignore — install still works with static manifest fallback */
  }
}

/** Apply club branding to CSS variables, document head, and optional localStorage. */
export function applyFullBrandTheme(brand, { persist = false } = {}) {
  const merged = sanitizeBrandLogoUrl({ ...DEFAULT_BRAND, ...(brand || {}) });
  injectBrandCssVariables(merged);
  updateBrandDocumentHead(merged);
  if (persist) {
    try {
      localStorage.setItem("brand_config", JSON.stringify(merged));
      if (merged.club_id != null) {
        localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, String(merged.club_id));
      }
    } catch {
      /* ignore */
    }
  }
  return merged;
}

export function isSuperAdminUser(user) {
  if (!user || typeof user !== "object") return false;
  if (user.isSuperAdmin === true) return true;
  if (Number(user.type_user_id) === 1) return true;
  const names = user.all_role_names;
  if (Array.isArray(names)) {
    const lower = names.map((n) => String(n).toLowerCase());
    if (lower.some((n) => n.includes("super admin") || n.includes("superadmin"))) return true;
  }
  const roles = user.all_roles;
  if (Array.isArray(roles) && roles.some((r) => String(r).toLowerCase() === "r1")) return true;
  return false;
}

/** Force SmartTT platform branding (club-agnostic). */
export function applyPlatformBrand() {
  const platform = applyFullBrandTheme(DEFAULT_BRAND, { persist: true });
  try {
    localStorage.removeItem(LAST_BRAND_CLUB_ID_KEY);
  } catch {
    /* ignore */
  }
  return platform;
}

export function resolveClubIdForBrand(user, clubIdHint) {
  if (!user) {
    return clubIdHint ? String(clubIdHint).trim() : null;
  }
  const fromUser =
    user.club_id ||
    user.club_details?.club_id ||
    user.club?.club_id;
  if (fromUser) return String(fromUser).trim();
  if (clubIdHint) return String(clubIdHint).trim();
  return user.brand_config?.club_id ? String(user.brand_config.club_id).trim() : null;
}

/** Drop another club's cached theme so it cannot flash after login. */
export function discardCachedBrandIfOtherClub(clubId) {
  if (!clubId) return;
  try {
    const raw = localStorage.getItem("brand_config");
    if (!raw) return;
    const prev = JSON.parse(raw);
    const prevClub = prev?.club_id != null ? String(prev.club_id) : "";
    if (prevClub && prevClub !== String(clubId)) {
      localStorage.removeItem("brand_config");
    }
  } catch {
    /* ignore */
  }
}

/** Apply club branding after sign-in or registration (same-tab; storage events do not fire). */
export async function syncBrandAfterAuth(user, refreshBrand, { clubIdHint } = {}) {
  if (isSuperAdminUser(user)) {
    applyPlatformBrand();
    if (typeof refreshBrand === "function") {
      await refreshBrand();
    }
    return;
  }

  const clubId = resolveClubIdForBrand(user, clubIdHint);
  if (clubId) {
    discardCachedBrandIfOtherClub(clubId);
    localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, clubId);
  }

  if (user?.brand_config && typeof user.brand_config === "object") {
    const lang = clubLanguageFromStoredUser(user);
    const merged = sanitizeBrandLogoUrl(
      applyCountryLangBrandDefaults(
        {
          ...DEFAULT_BRAND,
          ...user.brand_config,
          ...(clubId ? { club_id: clubId } : {}),
        },
        lang
      )
    );
    applyFullBrandTheme(merged, { persist: true });
  }

  if (typeof refreshBrand === "function") {
    await refreshBrand();
  }
}
